import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * EzoicAd - Reusable Ezoic ad placement component for React SPAs
 * 
 * Handles dynamic content and page transitions automatically.
 * 
 * Usage:
 *   <EzoicAd placementId={101} />
 * 
 * Placement IDs should be created in the Ezoic Dashboard:
 * https://pubdash.ezoic.com/ezoicads/adpositions/placeholders
 */
export default function EzoicAd({ placementId, className = "" }) {
  const adRef = useRef(null);

  useEffect(() => {
    // Check if Ezoic is loaded and show the ad
    if (typeof window !== 'undefined' && window.ezstandalone) {
      window.ezstandalone.cmd.push(function() {
        try {
          window.ezstandalone.showAds(placementId);
        } catch (e) {
          console.debug('Ezoic ad not loaded:', placementId);
        }
      });
    }

    // Cleanup - destroy placeholder when component unmounts
    return () => {
      if (typeof window !== 'undefined' && window.ezstandalone) {
        window.ezstandalone.cmd.push(function() {
          try {
            window.ezstandalone.destroyPlaceholders(placementId);
          } catch (e) {
            // Silent fail on cleanup
          }
        });
      }
    };
  }, [placementId]);

  // Don't add any styling to the placeholder div per Ezoic docs
  return (
    <div 
      ref={adRef}
      id={`ezoic-pub-ad-placeholder-${placementId}`}
      className={className}
      data-testid={`ezoic-ad-${placementId}`}
    />
  );
}

/**
 * EzoicPageRefresh - Component to handle ad refresh on React Router page changes
 * 
 * Place this once in your App.js or layout component to automatically
 * refresh ads when navigating between pages in a SPA.
 * 
 * Usage:
 *   function App() {
 *     return (
 *       <>
 *         <EzoicPageRefresh />
 *         <Routes>...</Routes>
 *       </>
 *     );
 *   }
 */
export function EzoicPageRefresh() {
  const location = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip the first render (initial page load)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // On page change, refresh all ads
    if (typeof window !== 'undefined' && window.ezstandalone) {
      window.ezstandalone.cmd.push(function() {
        try {
          // Calling showAds() with no arguments refreshes all placeholders on the new page
          window.ezstandalone.showAds();
        } catch (e) {
          console.debug('Ezoic page refresh failed');
        }
      });
    }
  }, [location.pathname]);

  return null; // This component doesn't render anything
}

/**
 * useEzoicRefresh - Hook to manually trigger ad refresh
 * 
 * Useful for modals, tabs, or other dynamic content changes
 * 
 * Usage:
 *   const refreshAds = useEzoicRefresh();
 *   
 *   // When new content loads:
 *   refreshAds([104, 105]); // Refresh specific placements
 *   refreshAds(); // Refresh all placements
 */
export function useEzoicRefresh() {
  return (placementIds = []) => {
    if (typeof window !== 'undefined' && window.ezstandalone) {
      window.ezstandalone.cmd.push(function() {
        try {
          if (placementIds.length > 0) {
            window.ezstandalone.showAds(...placementIds);
          } else {
            window.ezstandalone.showAds();
          }
        } catch (e) {
          console.debug('Ezoic refresh failed');
        }
      });
    }
  };
}

/**
 * useEzoicDestroy - Hook to destroy ad placements
 * 
 * Use when content with ads is hidden/removed
 * 
 * Usage:
 *   const destroyAds = useEzoicDestroy();
 *   
 *   // When closing a modal with ads:
 *   destroyAds([104, 105]);
 *   
 *   // Destroy all ads:
 *   destroyAds(); // or destroyAds('all')
 */
export function useEzoicDestroy() {
  return (placementIds = []) => {
    if (typeof window !== 'undefined' && window.ezstandalone) {
      window.ezstandalone.cmd.push(function() {
        try {
          if (placementIds === 'all' || placementIds.length === 0) {
            window.ezstandalone.destroyAll();
          } else {
            window.ezstandalone.destroyPlaceholders(...placementIds);
          }
        } catch (e) {
          console.debug('Ezoic destroy failed');
        }
      });
    }
  };
}
